import { useState, useEffect } from 'react'
import { Save, Loader2, Upload, ImageIcon, Copy, Plus, Ban, RefreshCw, KeyRound, Bot } from 'lucide-react'
import { settingsApi, uploadApi, mcpAdminApi, type McpKeyItem } from '../../lib/api'

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState<'basic' | 'feature' | 'sms' | 'commitment' | 'agreement' | 'mcp'>('basic')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // 短信调试
  const [smsDebug, setSmsDebug] = useState(false)
  const [smsLogs, setSmsLogs] = useState<any[]>([])
  const [smsTotal, setSmsTotal] = useState(0)
  const [smsPage, setSmsPage] = useState(1)
  const smsPageSize = 20

  // Logo上传
  const [logoUploading, setLogoUploading] = useState(false)

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setLogoUploading(true)
    try {
      const url = await uploadApi.uploadImage(file)
      setSettings(prev => ({ ...prev, systemLogo: url }))
    } catch {
      alert('Logo上传失败')
    } finally {
      setLogoUploading(false)
    }
  }

  const [settings, setSettings] = useState({
    systemName: '眉山疾控积分系统',
    systemTitle: '眉山健康积分',
    systemLogo: '',
    icpNumber: '蜀ICP备12345678号',
    copyright: '© 2026 眉山疾病预防控制中心',
    hotline: '028-12345678',
    maintenanceMode: false,
    maintenanceMessage: '系统维护中，请稍后再试',
    allowRegister: true,
    allowExchange: true,
    minPointsWithdraw: 100,
    showGiftStock: true,
    checkinEnabled: true,
    checkinPoints: 10,
    wechatLoginEnabled: true,
    // 科普知识入口
    knowledgeEnabled: true,
    // 每日阅读奖励
    contentReadRewardEnabled: true,
    contentReadRewardMinutes: 10,
    contentReadRewardPoints: 5,
    // 每月答题
    monthlyQuizEnabled: false,
    monthlyQuizQrCode: '',
    monthlyQuizAppName: '',
    // 小调查
    surveyEnabled: false,
    surveyQrCode: '',
    surveyAppName: '',
    smsAppId: '',
    smsAppSecret: '',
    smsSign: '',
    smsTemplateId: '',
    smsDebugMode: false
  })

  // 每月答题二维码上传
  const [quizQrUploading, setQuizQrUploading] = useState(false)
  const handleQuizQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('文件大小不能超过5MB')
      return
    }
    e.target.value = ''
    setQuizQrUploading(true)
    try {
      const url: string = await uploadApi.uploadImage(file)
      setSettings(prev => ({ ...prev, monthlyQuizQrCode: url }))
    } catch (err) {
      alert('上传失败：' + ((err as any)?.response?.data?.detail || (err as any)?.message || '未知错误'))
    } finally {
      setQuizQrUploading(false)
    }
  }

  // 小调查二维码上传
  const [surveyQrUploading, setSurveyQrUploading] = useState(false)
  const handleSurveyQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('文件大小不能超过5MB')
      return
    }
    e.target.value = ''
    setSurveyQrUploading(true)
    try {
      const url: string = await uploadApi.uploadImage(file)
      setSettings(prev => ({ ...prev, surveyQrCode: url }))
    } catch (err) {
      alert('上传失败：' + ((err as any)?.response?.data?.detail || (err as any)?.message || '未知错误'))
    } finally {
      setSurveyQrUploading(false)
    }
  }

  // 每月答题二维码上传
  // (quizQrUploading 和 handleQuizQrUpload 已声明在前面)

  const [commitmentSettings, setCommitmentSettings] = useState({
    content: '',
    enabled: false
  })

  const [agreementSettings, setAgreementSettings] = useState({
    userAgreement: '',
    privacyPolicy: ''
  })

  useEffect(() => {
    loadSettings()
  }, [activeTab])

  // 组件挂载时加载一次
  useEffect(() => {
    loadSettings()
  }, [])

  // 切换到短信tab时加载数据
  useEffect(() => {
    if (activeTab === 'sms') {
      fetch(`/api/v1/sms/debug-mode`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).then(r => r.json()).then(d => setSmsDebug(d.enabled ?? false))
      loadSmsLogs(1)
    }
  }, [activeTab])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const data = await settingsApi.getSystem()
      if (data) {
        setSettings(prev => ({ ...prev, ...data }))
        // 同步写入 localStorage，供 Layout 等全局组件使用
        if (data.systemName) localStorage.setItem('systemName', data.systemName)
        if (data.systemTitle) localStorage.setItem('systemTitle', data.systemTitle)
        if (data.copyright) localStorage.setItem('copyright', data.copyright)
      }
      // 加载每月答题配置
      try {
        const quizData: any = await settingsApi.getMonthlyQuiz()
        if (quizData) {
          setSettings(prev => ({
            ...prev,
            monthlyQuizEnabled: !!quizData.enabled,
            monthlyQuizQrCode: quizData.qrCodeUrl || '',
            monthlyQuizAppName: quizData.appName || ''
          }))
        }
      } catch (e) {
        console.warn('加载每月答题配置失败', e)
      }
      // 加载小调查配置
      try {
        const surveyData: any = await settingsApi.getSurvey()
        if (surveyData) {
          setSettings(prev => ({
            ...prev,
            surveyEnabled: !!surveyData.enabled,
            surveyQrCode: surveyData.qrCodeUrl || '',
            surveyAppName: surveyData.appName || ''
          }))
        }
      } catch (e) {
        console.warn('加载小调查配置失败', e)
      }
      // 加载每日阅读奖励配置
      try {
        const rr: any = await settingsApi.getContentReadReward()
        if (rr) {
          setSettings(prev => ({
            ...prev,
            contentReadRewardEnabled: !!rr.enabled,
            contentReadRewardMinutes: rr.minutes ?? 10,
            contentReadRewardPoints: rr.points ?? 5
          }))
        }
      } catch (e) {
        console.warn('加载每日阅读奖励配置失败', e)
      }
      const commitmentData = await settingsApi.getCommitment()
      if (commitmentData) {
        setCommitmentSettings({
          content: commitmentData.content || '',
          enabled: commitmentData.enabled || false
        })
      }
      const agreementData = await settingsApi.getAgreements()
      if (agreementData) {
        setAgreementSettings({
          userAgreement: agreementData.userAgreement || '',
          privacyPolicy: agreementData.privacyPolicy || ''
        })
      }
    } catch (e) {
      console.error('加载设置失败', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    // 校验：开启每月答题时，二维码和小程序名称必填
    if (settings.monthlyQuizEnabled) {
      if (!settings.monthlyQuizQrCode) {
        alert('开启「每月答题」请先上传小程序二维码')
        return
      }
      if (!settings.monthlyQuizAppName?.trim()) {
        alert('开启「每月答题」请填写小程序名称')
        return
      }
    }

    setSaving(true)
    try {
      // 每日阅读奖励走独立配置接口（保留 number/boolean 类型），不混入通用配置
      const { contentReadRewardEnabled, contentReadRewardMinutes, contentReadRewardPoints, ...baseSettings } = settings
      await settingsApi.updateSystem(baseSettings)
      // 保存后同步更新 localStorage
      localStorage.setItem('systemName', settings.systemName)
      localStorage.setItem('systemTitle', settings.systemTitle)
      localStorage.setItem('copyright', settings.copyright)
      // 单独保存每月答题配置
      await settingsApi.updateMonthlyQuiz({
        enabled: !!settings.monthlyQuizEnabled,
        qrCodeUrl: settings.monthlyQuizQrCode || '',
        appName: settings.monthlyQuizAppName || ''
      })
      // 单独保存小调查配置
      await settingsApi.updateSurvey({
        enabled: !!settings.surveyEnabled,
        qrCodeUrl: settings.surveyQrCode || '',
        appName: settings.surveyAppName || ''
      })
      // 单独保存每日阅读奖励配置
      await settingsApi.updateContentReadReward({
        enabled: !!contentReadRewardEnabled,
        minutes: Number(contentReadRewardMinutes) || 10,
        points: Number(contentReadRewardPoints) || 5
      })
      await loadSettings()
    } catch (e) {
      console.error('保存失败', e)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveCommitment = async () => {
    setSaving(true)
    try {
      await settingsApi.updateCommitment(commitmentSettings)
      await loadSettings()
    } catch (e) {
      console.error('保存承诺书配置失败', e)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAgreement = async () => {
    setSaving(true)
    try {
      await settingsApi.updateAgreements(agreementSettings)
      await loadSettings()
    } catch (e) {
      console.error('保存协议配置失败', e)
    } finally {
      setSaving(false)
    }
  }

  const toggleSmsDebug = async () => {
    try {
      const res = await fetch(`/api/v1/sms/debug-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ enabled: !smsDebug }),
      })
      if (res.ok) setSmsDebug(!smsDebug)
    } catch (e) { console.error(e) }
  }

  const loadSmsLogs = async (page = 1) => {
    setSmsPage(page)
    try {
      const res = await fetch(`/api/v1/sms/logs?page=${page}&page_size=${smsPageSize}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).then(r => r.json())
      setSmsLogs(res.items || [])
      setSmsTotal(res.total || 0)
    } catch (e) { console.error(e) }
  }

  // ===== 文章创作 MCP 管理 =====
  const [mcpLoading, setMcpLoading] = useState(false)
  const [mcpGlobalEnabled, setMcpGlobalEnabled] = useState(true)
  const [mcpKeys, setMcpKeys] = useState<McpKeyItem[]>([])
  const [newKeyName, setNewKeyName] = useState('')
  const [creatingKey, setCreatingKey] = useState(false)
  const [freshKey, setFreshKey] = useState<{ keyId: string; fullKey: string } | null>(null)

  const loadMcpKeys = async () => {
    setMcpLoading(true)
    try {
      const data = await mcpAdminApi.listKeys()
      setMcpKeys(data.items || [])
      setMcpGlobalEnabled(!!data.global_enabled)
    } catch (e) {
      console.error('加载MCP Key列表失败', e)
    } finally {
      setMcpLoading(false)
    }
  }

  // 切到 MCP tab 时加载
  useEffect(() => {
    if (activeTab === 'mcp') loadMcpKeys()
  }, [activeTab])

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      alert('请填写 Key 用途说明')
      return
    }
    setCreatingKey(true)
    try {
      const created = await mcpAdminApi.createKey({ name: newKeyName.trim() })
      setFreshKey({ keyId: created.key_id, fullKey: created.full_key || '' })
      setNewKeyName('')
      await loadMcpKeys()
    } catch (e: any) {
      alert('创建失败：' + (e?.message || '未知错误'))
    } finally {
      setCreatingKey(false)
    }
  }

  const handleRevokeKey = async (key: McpKeyItem) => {
    if (!confirm(`确定撤销 Key「${key.name || key.key_id}」吗？撤销后使用该 Key 的 AI 将立即失去访问权限，且不可恢复。`)) return
    try {
      await mcpAdminApi.revokeKey(key.id)
      await loadMcpKeys()
    } catch (e: any) {
      alert('撤销失败：' + (e?.message || '未知错误'))
    }
  }

  const handleToggleKey = async (key: McpKeyItem, field: 'enabled' | 'preview_enabled' | 'draft_create_enabled') => {
    try {
      await mcpAdminApi.updateKey(key.id, { [field]: !key[field] })
      await loadMcpKeys()
    } catch (e: any) {
      alert('更新失败：' + (e?.message || '未知错误'))
    }
  }

  const handleGlobalSwitch = async () => {
    try {
      await mcpAdminApi.setGlobalSwitch(!mcpGlobalEnabled)
      setMcpGlobalEnabled(!mcpGlobalEnabled)
    } catch (e: any) {
      alert('操作失败：' + (e?.message || '未知错误'))
    }
  }

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      alert('已复制到剪贴板')
    } catch {
      // 降级：兼容非 https 环境
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      alert('已复制')
    }
  }

  const scopeLabel = (scopes: string) => {
    const map: Record<string, string> = {
      capabilities: '能力查询',
      preview: '预览',
      draft: '草稿',
    }
    return scopes.split(',').map(s => map[s.trim()] || s.trim()).join(' / ')
  }

  const handleReset = () => {    setSettings({
      systemName: '眉山疾控积分系统',
      systemTitle: '眉山健康积分',
      icpNumber: '蜀ICP备12345678号',
      copyright: '© 2026 眉山疾病预防控制中心',
      hotline: '028-12345678',
      maintenanceMode: false,
      maintenanceMessage: '系统维护中，请稍后再试',
      allowRegister: true,
      allowExchange: true,
      minPointsWithdraw: 100,
      showGiftStock: true,
      checkinEnabled: true,
      checkinPoints: 10,
      knowledgeEnabled: true,
      contentReadRewardEnabled: true,
      contentReadRewardMinutes: 10,
      contentReadRewardPoints: 5,
      smsAppId: '',
      smsAppSecret: '',
      smsSign: '',
      smsTemplateId: ''
    })
  }

  const tabs = [
    { key: 'basic', label: '基础信息' },
    { key: 'feature', label: '功能配置' },
    { key: 'agreement', label: '协议配置' },
    { key: 'commitment', label: '承诺书' },
    { key: 'sms', label: '短信配置' },
    { key: 'mcp', label: '文章创作MCP' },
  ] as const

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">系统设置</h1>
          <p className="text-gray-500 text-sm mt-1">管理系统基本配置信息</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? '保存中...' : '保存配置'}</span>
          </button>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="bg-white rounded-2xl shadow-md p-1.5 inline-flex space-x-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-green-700 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab 内容 */}
      <div className="bg-white rounded-2xl shadow-md p-6">

        {/* ===== 基础信息 ===== */}
        {activeTab === 'basic' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo 图标</label>
              <div className="flex items-center space-x-6">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden border border-gray-200">
                  {logoUploading ? (
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  ) : settings.systemLogo ? (
                    <img src={settings.systemLogo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <label className={`px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors ${logoUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
                  <span className="flex items-center"><Upload className="w-4 h-4 mr-1" /> {logoUploading ? '上传中...' : '上传Logo'}</span>
                </label>
                {settings.systemLogo && (
                  <button onClick={() => setSettings(prev => ({ ...prev, systemLogo: '' }))} className="text-sm text-red-500 hover:underline">删除</button>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">建议尺寸 200×200，支持 PNG/JPG，建议白色背景</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">系统名称</label>
              <input
                type="text"
                value={settings.systemName}
                onChange={(e) => setSettings({ ...settings, systemName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">页面标题</label>
              <input
                type="text"
                value={settings.systemTitle}
                onChange={(e) => setSettings({ ...settings, systemTitle: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">联系电话</label>
              <input
                type="text"
                value={settings.hotline}
                onChange={(e) => setSettings({ ...settings, hotline: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ICP备案号</label>
              <input
                type="text"
                value={settings.icpNumber}
                onChange={(e) => setSettings({ ...settings, icpNumber: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">版权信息</label>
              <input
                type="text"
                value={settings.copyright}
                onChange={(e) => setSettings({ ...settings, copyright: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        )}

        {/* ===== 协议配置 ===== */}
        {activeTab === 'agreement' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-600">
              在此处编辑用户协议和隐私协议的内容，保存后用户端登录页的协议弹窗将显示最新内容。
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">用户协议</label>
              <textarea
                value={agreementSettings.userAgreement}
                onChange={(e) => setAgreementSettings({ ...agreementSettings, userAgreement: e.target.value })}
                rows={12}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                placeholder="请输入用户协议内容..."
              />
              <p className="text-xs text-gray-400 mt-1">支持换行符，将显示在登录页的《用户协议》弹窗中</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">隐私协议</label>
              <textarea
                value={agreementSettings.privacyPolicy}
                onChange={(e) => setAgreementSettings({ ...agreementSettings, privacyPolicy: e.target.value })}
                rows={12}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                placeholder="请输入隐私协议内容..."
              />
              <p className="text-xs text-gray-400 mt-1">支持换行符，将显示在登录页的《隐私协议》弹窗中</p>
            </div>

            <button
              onClick={handleSaveAgreement}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{saving ? '保存中...' : '保存协议配置'}</span>
            </button>
          </div>
        )}

        {/* ===== 承诺书配置 ===== */}
        {activeTab === 'commitment' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">启用承诺书签署</p>
                <p className="text-sm text-gray-500 mt-0.5">用户注册/登录时需要签署健康承诺书</p>
              </div>
              <button
                onClick={() => setCommitmentSettings({ ...commitmentSettings, enabled: !commitmentSettings.enabled })}
                className={`w-14 h-8 rounded-full transition-colors relative ${commitmentSettings.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${commitmentSettings.enabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">承诺书内容</label>
              <textarea
                value={commitmentSettings.content}
                onChange={(e) => setCommitmentSettings({ ...commitmentSettings, content: e.target.value })}
                rows={12}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                placeholder="请输入承诺书内容..."
              />
              <p className="text-xs text-gray-400 mt-1">支持换行符，每条承诺占一行</p>
            </div>

            <button
              onClick={handleSaveCommitment}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{saving ? '保存中...' : '保存承诺书配置'}</span>
            </button>
          </div>
        )}

        {/* ===== 功能配置 ===== */}
        {activeTab === 'feature' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 维护模式 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">维护模式</p>
                <p className="text-sm text-gray-500 mt-0.5">开启后用户将无法访问系统</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, maintenanceMode: !settings.maintenanceMode })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.maintenanceMode ? 'bg-red-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.maintenanceMode ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 允许注册 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">允许注册</p>
                <p className="text-sm text-gray-500 mt-0.5">控制新用户注册功能</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, allowRegister: !settings.allowRegister })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.allowRegister ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.allowRegister ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 允许兑换 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">允许兑换</p>
                <p className="text-sm text-gray-500 mt-0.5">控制积分兑换功能</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, allowExchange: !settings.allowExchange })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.allowExchange ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.allowExchange ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 展示礼品库存 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">展示礼品库存</p>
                <p className="text-sm text-gray-500 mt-0.5">用户端是否显示礼品库存数量</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, showGiftStock: !settings.showGiftStock })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.showGiftStock ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.showGiftStock ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 每日签到 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">开启每日签到</p>
                <p className="text-sm text-gray-500 mt-0.5">用户每天可签到一次获取积分</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, checkinEnabled: !settings.checkinEnabled })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.checkinEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.checkinEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 微信一键登录 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">微信一键登录</p>
                <p className="text-sm text-gray-500 mt-0.5">开启后登录页显示微信一键登录按钮</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, wechatLoginEnabled: !settings.wechatLoginEnabled })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.wechatLoginEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.wechatLoginEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 科普知识入口 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">展示科普知识入口</p>
                <p className="text-sm text-gray-500 mt-0.5">开启后用户端底部导航显示「科普」入口，关闭则隐藏</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, knowledgeEnabled: !settings.knowledgeEnabled })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.knowledgeEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.knowledgeEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 每日阅读奖励 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">每日阅读奖励</p>
                <p className="text-sm text-gray-500 mt-0.5">用户每天累计阅读健康科普达到设定时长，奖励一次积分</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, contentReadRewardEnabled: !settings.contentReadRewardEnabled })}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.contentReadRewardEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.contentReadRewardEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 阅读奖励参数（开关开启时展示） */}
            {settings.contentReadRewardEnabled && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">阅读时长达标（分钟）</label>
                  <input
                    type="number"
                    value={settings.contentReadRewardMinutes}
                    onChange={(e) => setSettings({ ...settings, contentReadRewardMinutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="1"
                    max="1440"
                  />
                  <p className="text-xs text-gray-400 mt-1">用户当天累计阅读达到该分钟数后自动发放奖励</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">奖励积分</label>
                  <input
                    type="number"
                    value={settings.contentReadRewardPoints}
                    onChange={(e) => setSettings({ ...settings, contentReadRewardPoints: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="1"
                  />
                  <p className="text-xs text-gray-400 mt-1">达标后一次性发放的积分，每天最多奖励 1 次</p>
                </div>
              </>
            )}

            {/* 最低兑换积分 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">最低兑换积分</label>
              <input
                type="number"
                value={settings.minPointsWithdraw}
                onChange={(e) => setSettings({ ...settings, minPointsWithdraw: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="text-xs text-gray-400 mt-1">用户积分达到此值才能进行兑换</p>
            </div>

            {/* 签到奖励积分 */}
            {settings.checkinEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">签到奖励积分</label>
                <input
                  type="number"
                  value={settings.checkinPoints}
                  onChange={(e) => setSettings({ ...settings, checkinPoints: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  min="1"
                />
                <p className="text-xs text-gray-400 mt-1">用户每次签到获得的积分数量</p>
              </div>
            )}

            {/* 维护提示语（条件展示，配占左侧） */}
            {settings.maintenanceMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">维护提示语</label>
                <textarea
                  value={settings.maintenanceMessage}
                  onChange={(e) => setSettings({ ...settings, maintenanceMessage: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
              </div>
            )}

            {/* 每月答题（独立分组） */}
            <div className="md:col-span-2 mt-2 p-4 bg-white border border-gray-200 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">每月答题</p>
                  <p className="text-xs text-gray-500 mt-0.5">开启后用户活动页面左下角显示「每月答题」悬浮按钮</p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, monthlyQuizEnabled: !settings.monthlyQuizEnabled })}
                  className={`w-12 h-7 rounded-full transition-colors relative ${settings.monthlyQuizEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.monthlyQuizEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.monthlyQuizEnabled && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  {/* 二维码上传（窄尺寸） */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      小程序二维码 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <label className="cursor-pointer flex-shrink-0">
                        <div className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors flex items-center space-x-1.5 text-xs text-gray-700">
                          {quizQrUploading ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>上传中</span></>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /><span>选择图片</span></>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={quizQrUploading}
                          onChange={handleQuizQrUpload}
                        />
                      </label>
                      {settings.monthlyQuizQrCode && (
                        <div className="w-10 h-10 rounded-md overflow-hidden border border-gray-200 bg-white flex-shrink-0">
                          <img src={settings.monthlyQuizQrCode} alt="二维码" className="w-full h-full object-contain" />
                        </div>
                      )}
                      {settings.monthlyQuizQrCode && (
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, monthlyQuizQrCode: '' })}
                          className="text-xs text-red-500 hover:underline flex-shrink-0"
                        >
                          清除
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">jpg/png/webp，≤5MB</p>
                  </div>

                  {/* 小程序名称 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      小程序名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={settings.monthlyQuizAppName}
                      onChange={(e) => setSettings({ ...settings, monthlyQuizAppName: e.target.value })}
                      placeholder="例如：眉山疾控答题"
                      maxLength={30}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">用户扫码后看到的小程序名称</p>
                  </div>
                </div>
              )}
            </div>

            {/* 小调查（独立分组） */}
            <div className="md:col-span-2 mt-2 p-4 bg-white border border-gray-200 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">小调查</p>
                  <p className="text-xs text-gray-500 mt-0.5">开启后用户会员中心左下角显示「小调查」悬浮按钮</p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, surveyEnabled: !settings.surveyEnabled })}
                  className={`w-12 h-7 rounded-full transition-colors relative ${settings.surveyEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.surveyEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {settings.surveyEnabled && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  {/* 二维码上传 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      小程序二维码 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <label className="cursor-pointer flex-shrink-0">
                        <div className="px-3 py-1.5 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors flex items-center space-x-1.5 text-xs text-gray-700">
                          {surveyQrUploading ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>上传中</span></>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /><span>选择图片</span></>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={surveyQrUploading}
                          onChange={handleSurveyQrUpload}
                        />
                      </label>
                      {settings.surveyQrCode && (
                        <div className="w-10 h-10 rounded-md overflow-hidden border border-gray-200 bg-white flex-shrink-0">
                          <img src={settings.surveyQrCode} alt="小调查二维码" className="w-full h-full object-contain" />
                        </div>
                      )}
                      {settings.surveyQrCode && (
                        <button
                          type="button"
                          onClick={() => setSettings({ ...settings, surveyQrCode: '' })}
                          className="text-xs text-red-500 hover:underline flex-shrink-0"
                        >
                          清除
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">jpg/png/webp，≤5MB</p>
                  </div>

                  {/* 小程序名称 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      小程序名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={settings.surveyAppName}
                      onChange={(e) => setSettings({ ...settings, surveyAppName: e.target.value })}
                      placeholder="例如：眉山疾控调查"
                      maxLength={30}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">用户扫码后看到的小程序名称</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== 短信配置 ===== */}
        {activeTab === 'sms' && (
          <div className="space-y-6">
            {/* 调试模式开关 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">短信调试模式</p>
                <p className="text-sm text-gray-500 mt-0.5">开启后短信不真实发送，返回模拟成功，方便测试</p>
              </div>
              <button
                onClick={toggleSmsDebug}
                className={`w-14 h-8 rounded-full transition-colors relative ${smsDebug ? 'bg-red-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${smsDebug ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
            {smsDebug && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
                当前为调试模式，短信不会真实发送，请测试完毕后关闭调试模式
              </div>
            )}
            {/* 发送记录 */}
            <div className="bg-white rounded-2xl shadow overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <p className="font-medium text-gray-700">发送记录（{smsTotal} 条）</p>
                <button onClick={() => loadSmsLogs(1)} className="text-sm text-green-600 hover:underline">刷新</button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">手机号</th>
                    <th className="px-4 py-3 text-left">用途</th>
                    <th className="px-4 py-3 text-left">结果</th>
                    <th className="px-4 py-3 text-left">验证码</th>
                    <th className="px-4 py-3 text-left">IP</th>
                    <th className="px-4 py-3 text-left">发送时间</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {smsLogs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-gray-400">暂无记录</td></tr>
                  ) : smsLogs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{log.phone}</td>
                      <td className="px-4 py-3 text-gray-500">{log.purpose}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          log.send_result === 'ok' ? 'bg-green-100 text-green-700' :
                          log.send_result === 'mock' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {log.send_result === 'ok' ? '成功' : log.send_result === 'mock' ? '调试' : '失败'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono">{log.code || '-'}</td>
                      <td className="px-4 py-3 text-gray-400">{log.ip_address || '-'}</td>
                      <td className="px-4 py-3 text-gray-400">
                        {log.created_at ? new Date(log.created_at).toLocaleString('zh-CN') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* 分页 */}
              {Math.ceil(smsTotal / smsPageSize) > 1 && (
                <div className="flex items-center justify-center space-x-4 py-3 border-t">
                  <button
                    onClick={() => loadSmsLogs(smsPage - 1)}
                    disabled={smsPage <= 1}
                    className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40"
                  >上一页</button>
                  <span className="text-sm text-gray-500">
                    第 {smsPage} / {Math.ceil(smsTotal / smsPageSize)} 页
                  </span>
                  <button
                    onClick={() => loadSmsLogs(smsPage + 1)}
                    disabled={smsPage >= Math.ceil(smsTotal / smsPageSize)}
                    className="px-4 py-2 text-sm border rounded-lg disabled:opacity-40"
                  >下一页</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== 文章创作 MCP ===== */}
        {activeTab === 'mcp' && (
          <div className="space-y-6">
            {/* 功能说明 */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700 space-y-1">
              <p className="flex items-center font-medium"><Bot className="w-4 h-4 mr-1.5" />文章创作 MCP 服务</p>
              <p className="text-blue-600">允许本地 AI（WorkBuddy）通过 MCP 协议创作科普文章。<strong>AI 只能创建和修改草稿，发布必须由管理员在内容管理中人工审核完成。</strong>Key 明文仅在创建时显示一次，请立即保存。</p>
            </div>

            {/* 全局开关 */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-800">MCP 服务总开关</p>
                <p className="text-sm text-gray-500 mt-0.5">紧急停止用：关闭后所有 Key 立即失效，AI 无法再访问文章接口</p>
              </div>
              <button
                onClick={handleGlobalSwitch}
                className={`w-14 h-8 rounded-full transition-colors relative ${mcpGlobalEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${mcpGlobalEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* 新建 Key */}
            <div className="p-4 border border-gray-200 rounded-xl space-y-3">
              <p className="font-medium text-gray-800 flex items-center"><KeyRound className="w-4 h-4 mr-1.5 text-gray-500" />生成新 API Key</p>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="用途说明，例如：WorkBuddy 本地创作"
                  maxLength={50}
                  className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateKey()}
                />
                <button
                  onClick={handleCreateKey}
                  disabled={creatingKey}
                  className="flex items-center space-x-1.5 px-4 py-2.5 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {creatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  <span>{creatingKey ? '生成中...' : '生成 Key'}</span>
                </button>
              </div>
              <p className="text-xs text-gray-400">默认授权：能力查询 + 预览 + 草稿；限流 30 次/分钟。Key 生成后请配置到 WorkBuddy 的 mcp.json 中。</p>
            </div>

            {/* 新 Key 明文提示（仅创建后显示） */}
            {freshKey && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-amber-800">🎉 新 Key 已生成（key_id: {freshKey.keyId}），明文仅此一次显示，请立即复制保存：</p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 px-3 py-2 bg-white border border-amber-200 rounded-lg text-xs font-mono break-all text-gray-800">{freshKey.fullKey}</code>
                  <button
                    onClick={() => copyText(freshKey.fullKey)}
                    className="flex items-center space-x-1 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-xs whitespace-nowrap"
                  >
                    <Copy className="w-3.5 h-3.5" />复制
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-amber-600">配置位置：WorkBuddy → mcp.json → mcpServers.article-mcp.env.ARTICLE_MCP_API_KEY</p>
                  <button onClick={() => setFreshKey(null)} className="text-xs text-amber-700 hover:underline">我已保存，关闭</button>
                </div>
              </div>
            )}

            {/* Key 列表 */}
            <div className="bg-white rounded-2xl shadow overflow-hidden border border-gray-100">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <p className="font-medium text-gray-700">API Key 列表（{mcpKeys.length} 个）</p>
                <button onClick={loadMcpKeys} className="flex items-center text-sm text-green-600 hover:underline">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${mcpLoading ? 'animate-spin' : ''}`} />刷新
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-4 py-3 text-left">用途</th>
                      <th className="px-4 py-3 text-left">Key ID</th>
                      <th className="px-4 py-3 text-left">授权范围</th>
                      <th className="px-4 py-3 text-left">状态开关</th>
                      <th className="px-4 py-3 text-left">最近使用</th>
                      <th className="px-4 py-3 text-left">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {mcpKeys.length === 0 ? (
                      <tr><td colSpan={6} className="text-center py-12 text-gray-400">暂无 Key，先生成一个</td></tr>
                    ) : mcpKeys.map(k => (
                      <tr key={k.id} className={`hover:bg-gray-50 ${k.revoked ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{k.name || '未命名'}</p>
                          <p className="text-xs text-gray-400 font-mono mt-0.5">{k.key_id}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{scopeLabel(k.scopes)}</td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          <p>限流 {k.rate_limit_per_minute}/分钟</p>
                          {k.created_at && <p>创建于 {new Date(k.created_at).toLocaleDateString('zh-CN')}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {k.revoked ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">已撤销</span>
                          ) : (
                            <div className="flex flex-col space-y-1.5">
                              {/* 总开关 */}
                              <label className="flex items-center space-x-1.5 cursor-pointer">
                                <button
                                  onClick={() => handleToggleKey(k, 'enabled')}
                                  className={`w-9 h-5 rounded-full transition-colors relative ${k.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${k.enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-xs text-gray-600">启用</span>
                              </label>
                              <label className="flex items-center space-x-1.5 cursor-pointer" title="允许 AI 生成预览">
                                <button
                                  onClick={() => handleToggleKey(k, 'preview_enabled')}
                                  className={`w-9 h-5 rounded-full transition-colors relative ${k.preview_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${k.preview_enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-xs text-gray-600">预览</span>
                              </label>
                              <label className="flex items-center space-x-1.5 cursor-pointer" title="允许 AI 创建/修改草稿">
                                <button
                                  onClick={() => handleToggleKey(k, 'draft_create_enabled')}
                                  className={`w-9 h-5 rounded-full transition-colors relative ${k.draft_create_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                                >
                                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${k.draft_create_enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-xs text-gray-600">草稿</span>
                              </label>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {k.last_used_at ? new Date(k.last_used_at).toLocaleString('zh-CN') : '从未使用'}
                        </td>
                        <td className="px-4 py-3">
                          {!k.revoked && (
                            <button
                              onClick={() => handleRevokeKey(k)}
                              className="flex items-center text-xs text-red-500 hover:underline"
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />撤销
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 接入说明 */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-600 space-y-2">
              <p className="font-medium text-gray-700">WorkBuddy 接入方式</p>
              <p>1. 生成 Key 并复制明文</p>
              <p>2. 在 WorkBuddy 的 mcp.json 中配置（配置后需在连接器管理中点击「信任」）：</p>
              <pre className="bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto font-mono text-[11px] leading-5">{`{
  "mcpServers": {
    "article-mcp": {
      "type": "stdio",
      "command": "C:/Python313/python.exe",
      "args": ["D:/wwwroot/ms-jiankangjifen/ms-yijiajifen/tools/article_mcp_server/server.py"],
      "env": {
        "ARTICLE_MCP_BACKEND_URL": "http://127.0.0.1:8001",
        "ARTICLE_MCP_API_KEY": "<粘贴你的Key>"
      }
    }
  }
}`}</pre>
              <p>3. AI 创建的草稿出现在 <span className="text-green-700 font-medium">内容管理</span> 中，状态为「草稿」，审核通过后才会上线</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SystemSettings
